/**
 * Page Surveillance — prise d'appel optimisée tablette + mobile.
 *
 * Flow :
 *  1. Date (défaut aujourd'hui)
 *  2. Liste des séances du jour OU choix d'un groupe tronc commun
 *  3. Sélection → écran d'appel : recherche, swipe, boutons rapides, footer
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ClipboardList, Calendar, CheckCircle2, Clock, XCircle, FileCheck, AlertTriangle,
  ChevronLeft, ChevronRight, Search, Save, ArrowLeft, Users, BookOpen, User,
  Layers, MapPin, Trash2, CalendarClock, RotateCw,
} from 'lucide-react';
import {
  useListerSeancesQuery, useDetailAppelQuery, useValiderAppelMutation,
  useDetailAppelTroncCommunQuery, useValiderAppelTroncCommunMutation,
  useDetailAppelTroncCommunImpliciteQuery, useValiderAppelTroncCommunImpliciteMutation,
  useSupprimerSeanceMutation, useReporterSeanceMutation,
} from '../../services/vieScolaireApi';
import type { AppelEtudiantItem, SeanceItem } from '../../services/vieScolaireApi';
import EtudiantAppelRow from './EtudiantAppelRow';
import ModalDetailPresence from './ModalDetailPresence';
import ModalReporterSeance from './ModalReporterSeance';

type Statut = AppelEtudiantItem['statut'];

const STATUT_META: Record<Statut, { label: string; couleur: string; Icon: any }> = {
  present:  { label: 'Présent',   couleur: 'text-green-500',  Icon: CheckCircle2 },
  retard:   { label: 'Retard',    couleur: 'text-amber-500',  Icon: Clock },
  absent:   { label: 'Absent',    couleur: 'text-red-500',    Icon: XCircle },
  justifie: { label: 'Justifiée', couleur: 'text-blue-400',   Icon: FileCheck },
  incident: { label: 'Incident',  couleur: 'text-orange-500', Icon: AlertTriangle },
};

function isoJour(d: Date): string { return d.toISOString().slice(0, 10); }

export default function SurveillancePage() {
  const [date, setDate] = useState<string>(isoJour(new Date()));
  const [selection, setSelection] = useState<
    | { kind: 'seance'; id: number }
    | { kind: 'tronc'; groupe: string }
    | { kind: 'tronc-implicite'; matiere: string; heure_debut: string; formateur: string }
    | null
  >(null);
  const [reportingId, setReportingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const { data, isFetching, refetch } = useListerSeancesQuery({ depuis: date, jusqua: date });
  const [supprimerSeance] = useSupprimerSeanceMutation();
  const seances = data?.results || [];

  const seanceEnReport = useMemo(
    () => seances.find(s => s.id === reportingId) || null,
    [seances, reportingId],
  );

  const handleSupprimerSeance = async (id: number, libelle: string) => {
    if (!window.confirm(`Supprimer la séance « ${libelle} » ?\n\nSi l'appel a déjà été fait, elle sera marquée comme annulée (les présences sont conservées).`)) return;
    try {
      const res = await supprimerSeance(id).unwrap();
      refetch();
      if (res.mode === 'annulee') {
        alert('Séance annulée (présences préservées pour audit).');
      }
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur lors de la suppression.');
    }
  };

  // Filtre par recherche d'abord (matière, classe, formateur, salle)
  const seancesFiltrees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return seances;
    return seances.filter(s =>
      s.matiere.toLowerCase().includes(q)
      || s.classe.toLowerCase().includes(q)
      || (s.formateur || '').toLowerCase().includes(q)
      || (s.salle || '').toLowerCase().includes(q)
    );
  }, [seances, search]);

  // Groupes tronc commun EXPLICITES (via tronc_commun_groupe)
  const groupesExplicites = useMemo(() => {
    const map = new Map<string, typeof seancesFiltrees>();
    for (const s of seancesFiltrees) {
      if (s.tronc_commun_groupe) {
        const arr = map.get(s.tronc_commun_groupe) || [];
        arr.push(s);
        map.set(s.tronc_commun_groupe, arr);
      }
    }
    return Array.from(map.entries())
      .filter(([, arr]) => arr.length >= 2)
      .map(([groupe, arr]) => {
        const ref = arr[0];
        return {
          kind: 'explicite' as const,
          cle: `g:${groupe}`,
          groupe,
          matiere: ref.matiere,
          formateur: ref.formateur,
          heure_debut: ref.heure_debut,
          heure_fin: ref.heure_fin,
          salle: ref.salle,
          classes: arr.map(s => s.classe).sort(),
          nb_appel_fait: arr.filter(s => s.appel_fait).length,
          nb_present: arr.reduce((acc, s) => acc + (s.nb_present || 0), 0),
          nb_absent: arr.reduce((acc, s) => acc + (s.nb_absent || 0), 0),
          nb_justifie: arr.reduce((acc, s) => acc + (s.nb_justifie || 0), 0),
        };
      });
  }, [seancesFiltrees]);

  // Groupes tronc commun IMPLICITES (même jour + heure + matière + formateur, sans groupe)
  const groupesImplicites = useMemo(() => {
    const map = new Map<string, typeof seancesFiltrees>();
    for (const s of seancesFiltrees) {
      if (s.tronc_commun_groupe) continue; // déjà traité dans explicites
      // Clé : matière|heure_debut|formateur (on suppose même formateur = vrai TC)
      const key = `${s.matiere}|${s.heure_debut}|${s.formateur || ''}`;
      const arr = map.get(key) || [];
      arr.push(s);
      map.set(key, arr);
    }
    return Array.from(map.entries())
      .filter(([, arr]) => arr.length >= 2)
      .map(([key, arr]) => {
        const ref = arr[0];
        return {
          kind: 'implicite' as const,
          cle: `i:${key}`,
          matiere: ref.matiere,
          formateur: ref.formateur,
          heure_debut: ref.heure_debut,
          heure_fin: ref.heure_fin,
          salle: ref.salle,
          classes: arr.map(s => s.classe).sort(),
          nb_appel_fait: arr.filter(s => s.appel_fait).length,
          nb_present: arr.reduce((acc, s) => acc + (s.nb_present || 0), 0),
          nb_absent: arr.reduce((acc, s) => acc + (s.nb_absent || 0), 0),
          nb_justifie: arr.reduce((acc, s) => acc + (s.nb_justifie || 0), 0),
        };
      });
  }, [seancesFiltrees]);

  const groupesTroncCommun = useMemo(
    () => [...groupesExplicites, ...groupesImplicites],
    [groupesExplicites, groupesImplicites],
  );

  // Filtre les séances "simples" (ni groupe explicite, ni implicite avec >=2)
  const seancesSimples = useMemo(() => {
    const classesDansGroupe = new Set<string>();
    for (const g of groupesTroncCommun) {
      for (const c of g.classes) {
        classesDansGroupe.add(`${g.matiere}|${g.heure_debut}|${c}`);
      }
    }
    return seancesFiltrees.filter(s =>
      !classesDansGroupe.has(`${s.matiere}|${s.heure_debut}|${s.classe}`),
    );
  }, [seancesFiltrees, groupesTroncCommun]);

  if (selection?.kind === 'seance') {
    return <EcranAppelSeance seanceId={selection.id} onClose={() => { setSelection(null); refetch(); }} />;
  }
  if (selection?.kind === 'tronc') {
    return <EcranAppelTroncCommun date={date} groupe={selection.groupe} onClose={() => { setSelection(null); refetch(); }} />;
  }
  if (selection?.kind === 'tronc-implicite') {
    return <EcranAppelTroncCommunImplicite
      date={date}
      matiere={selection.matiere}
      heure_debut={selection.heure_debut}
      formateur={selection.formateur}
      onClose={() => { setSelection(null); refetch(); }} />;
  }

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* En-tête */}
      <header className="mb-4 sm:mb-5">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardList size={22} className="text-blue-600 dark:text-blue-400" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Surveillance &amp; Présence
          </h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
          Sélectionnez une séance pour démarrer l'appel.
        </p>
      </header>

      {/* Barre date */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={() => { const d = new Date(date); d.setDate(d.getDate() - 1); setDate(isoJour(d)); }}
          className="p-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg">
          <ChevronLeft size={16} />
        </button>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 flex-1 sm:flex-initial min-w-0" />
        <button onClick={() => { const d = new Date(date); d.setDate(d.getDate() + 1); setDate(isoJour(d)); }}
          className="p-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg">
          <ChevronRight size={16} />
        </button>
        <button onClick={() => setDate(isoJour(new Date()))}
          className="px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg whitespace-nowrap">
          Aujourd'hui
        </button>
        <button onClick={() => refetch()} disabled={isFetching}
          className="ml-auto px-3 py-2 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg whitespace-nowrap">
          {isFetching ? '…' : '↻ Actualiser'}
        </button>
      </div>

      {/* Barre de recherche */}
      {seances.length > 0 && (
        <div className="mb-4 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une matière, classe, formateur ou salle…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      )}

      {/* Liste */}
      {isFetching && seances.length === 0 && (
        <div className="text-center py-10 text-sm text-gray-500">Chargement…</div>
      )}

      {!isFetching && seances.length === 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 sm:p-10 text-center">
          <Calendar size={34} strokeWidth={1.3} className="mx-auto mb-3 text-gray-400 dark:text-gray-600" />
          <div className="text-gray-700 dark:text-gray-300 text-sm font-medium">Aucune séance prévue ce jour.</div>
          <div className="text-xs text-gray-500 mt-1.5">
            Saisissez l'emploi du temps dans <strong>Emplois du temps (créneaux)</strong>, puis générez les séances.
          </div>
        </div>
      )}

      {groupesTroncCommun.length > 0 && (
        <div className="mb-5">
          <div className="text-[11px] uppercase font-semibold text-gray-500 dark:text-gray-400 mb-2 tracking-wider flex items-center gap-1.5">
            <Layers size={11} /> Troncs communs ({groupesTroncCommun.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {groupesTroncCommun.map(g => (
              <button key={g.cle}
                onClick={() => {
                  if (g.kind === 'explicite') {
                    setSelection({ kind: 'tronc', groupe: g.groupe });
                  } else {
                    setSelection({ kind: 'tronc-implicite', matiere: g.matiere, heure_debut: g.heure_debut, formateur: g.formateur || '' });
                  }
                }}
                className={`text-left bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/70 border rounded-xl p-3 transition ${
                  g.kind === 'implicite'
                    ? 'border-amber-300 dark:border-amber-700/60'
                    : 'border-gray-200 dark:border-gray-700'
                }`}>
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">{g.matiere}</div>
                  <div className={`text-[10px] px-1.5 py-0.5 rounded ${
                    g.kind === 'implicite'
                      ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                      : 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                  }`}>
                    {g.kind === 'implicite' ? `${g.classes.length} cl. (auto)` : `${g.classes.length} classes`}
                  </div>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1 mb-1">
                  <Clock size={11} /> {g.heure_debut}–{g.heure_fin}
                  {g.salle && <><span className="mx-1">•</span><MapPin size={11} /> {g.salle}</>}
                </div>
                {g.formateur && (
                  <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1 mb-1">
                    <User size={11} /> {g.formateur}
                  </div>
                )}
                <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                  {g.classes.join(' • ')}
                </div>
                <div className="text-[10px] mt-1.5 flex items-center gap-1.5 flex-wrap">
                  {g.nb_appel_fait === g.classes.length ? (
                    <>
                      <span className="text-green-600 dark:text-green-400 font-medium">Appel fait</span>
                      <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                        <CheckCircle2 size={10} /> {g.nb_present}
                      </span>
                      {g.nb_absent > 0 && (
                        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                          <XCircle size={10} /> {g.nb_absent}
                        </span>
                      )}
                      {g.nb_justifie > 0 && (
                        <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                          <FileCheck size={10} /> {g.nb_justifie}
                        </span>
                      )}
                    </>
                  ) : g.nb_appel_fait > 0 ? (
                    <span className="text-amber-600 dark:text-amber-400">Partiel ({g.nb_appel_fait}/{g.classes.length})</span>
                  ) : (
                    <span className="text-gray-500">En attente</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {seancesSimples.length > 0 && (
        <div>
          <div className="text-[11px] uppercase font-semibold text-gray-500 dark:text-gray-400 mb-2 tracking-wider flex items-center gap-1.5">
            <BookOpen size={11} /> Séances par classe ({seancesSimples.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {seancesSimples.map(s => (
              <div key={s.id} className={`group relative bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/70 border rounded-xl transition ${
                s.est_rattrapage
                  ? 'border-amber-300 dark:border-amber-700/60'
                  : s.statut === 'reportee'
                    ? 'border-gray-300 dark:border-gray-700 opacity-60'
                    : 'border-gray-200 dark:border-gray-700'
              }`}>
                <button onClick={() => setSelection({ kind: 'seance', id: s.id })}
                  className="w-full text-left p-3"
                  disabled={s.statut === 'reportee'}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-semibold text-sm text-gray-900 dark:text-white truncate pr-6 flex items-center gap-1.5">
                      {s.matiere}
                      {s.est_rattrapage && (
                        <span className="text-[9px] px-1 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded inline-flex items-center gap-0.5">
                          <CalendarClock size={9} /> Rattrapage
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded">
                      {s.classe}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1 flex-wrap">
                    <Clock size={11} /> {s.heure_debut}–{s.heure_fin}
                    {s.salle && <><span className="mx-1">•</span><MapPin size={11} /> {s.salle}</>}
                  </div>
                  {s.formateur && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                      <User size={11} /> {s.formateur}
                    </div>
                  )}
                  <div className="text-[10px] mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {s.statut === 'annulee' ? (
                      <span className="text-red-500">Annulée</span>
                    ) : s.statut === 'reportee' ? (
                      <span className="text-amber-600 dark:text-amber-400">Reportée</span>
                    ) : s.appel_fait ? (
                      <>
                        <span className="text-green-600 dark:text-green-400 font-medium">Appel fait</span>
                        {typeof s.nb_present === 'number' && (
                          <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                            <CheckCircle2 size={10} /> {s.nb_present}
                          </span>
                        )}
                        {typeof s.nb_absent === 'number' && s.nb_absent > 0 && (
                          <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                            <XCircle size={10} /> {s.nb_absent}
                          </span>
                        )}
                        {typeof s.nb_justifie === 'number' && s.nb_justifie > 0 && (
                          <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                            <FileCheck size={10} /> {s.nb_justifie}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500">En attente</span>
                    )}
                  </div>
                </button>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 transition">
                  {!s.appel_fait && s.statut !== 'reportee' && s.statut !== 'annulee' && (
                    <button onClick={(e) => { e.stopPropagation(); setReportingId(s.id); }}
                      className="p-1.5 text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded"
                      title="Reporter (rattrapage)">
                      <CalendarClock size={13} />
                    </button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); handleSupprimerSeance(s.id, s.matiere); }}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                    title="Supprimer la séance">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {seanceEnReport && (
        <ModalReporterSeance
          seance={seanceEnReport}
          onClose={() => setReportingId(null)}
          onReported={() => { setReportingId(null); refetch(); }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ÉCRAN D'APPEL — séance unique
// ────────────────────────────────────────────────────────────────────────────

function EcranAppelSeance({ seanceId, onClose }: { seanceId: number; onClose: () => void }) {
  const { data, isFetching } = useDetailAppelQuery(seanceId);
  const [valider, { isLoading: valIsLoading }] = useValiderAppelMutation();
  const seance = data?.seance;
  const etudiants = data?.etudiants || [];

  if (!data && isFetching) return <ChargementPlein label="Chargement de l'appel…" />;
  if (!seance) return <ChargementPlein label="Séance introuvable" />;

  return (
    <EcranAppel
      titre={seance.matiere}
      sousTitre={`${seance.classe} • ${seance.heure_debut}–${seance.heure_fin}`}
      formateur={seance.formateur}
      salle={seance.salle}
      etudiantsBruts={etudiants}
      modeClasse
      onClose={onClose}
      onValider={async (presences) => {
        await valider({ seanceId, presences }).unwrap();
      }}
      isValidating={valIsLoading}
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ÉCRAN D'APPEL — tronc commun
// ────────────────────────────────────────────────────────────────────────────

function EcranAppelTroncCommun({ date, groupe, onClose }: { date: string; groupe: string; onClose: () => void }) {
  const { data, isFetching } = useDetailAppelTroncCommunQuery({ date, groupe });
  const [valider, { isLoading: valIsLoading }] = useValiderAppelTroncCommunMutation();

  if (!data && isFetching) return <ChargementPlein label="Chargement du tronc commun…" />;
  if (!data) return <ChargementPlein label="Aucune donnée" />;

  return (
    <EcranAppel
      titre={data.matiere}
      sousTitre={`${data.classes.join(' • ')} • ${data.heure_debut}–${data.heure_fin}`}
      formateur={data.formateur}
      salle={data.salle}
      etudiantsBruts={data.etudiants}
      modeClasse={false}
      onClose={onClose}
      onValider={async (presences) => {
        await valider({ date, groupe, presences: presences.map(p => ({ ...p, classe: p.classe || '' })) }).unwrap();
      }}
      isValidating={valIsLoading}
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ÉCRAN D'APPEL — tronc commun IMPLICITE (auto-détecté)
// ────────────────────────────────────────────────────────────────────────────

function EcranAppelTroncCommunImplicite({ date, matiere, heure_debut, formateur, onClose }: {
  date: string; matiere: string; heure_debut: string; formateur: string; onClose: () => void;
}) {
  const { data, isFetching } = useDetailAppelTroncCommunImpliciteQuery({ date, matiere, heure_debut, formateur });
  const [valider, { isLoading: valIsLoading }] = useValiderAppelTroncCommunImpliciteMutation();

  if (!data && isFetching) return <ChargementPlein label="Chargement du tronc commun (auto)…" />;
  if (!data) return <ChargementPlein label="Aucune donnée" />;

  return (
    <EcranAppel
      titre={data.matiere}
      sousTitre={`${data.classes.join(' • ')} • ${data.heure_debut}–${data.heure_fin} (auto-détecté)`}
      formateur={data.formateur}
      salle={data.salle}
      etudiantsBruts={data.etudiants}
      modeClasse={false}
      onClose={onClose}
      onValider={async (presences) => {
        await valider({
          seance_ids: data.seance_ids,
          presences: presences.map(p => ({ ...p, classe: p.classe || '' })),
        }).unwrap();
      }}
      isValidating={valIsLoading}
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// COMPOSANT GÉNÉRIQUE D'APPEL
// ────────────────────────────────────────────────────────────────────────────

interface PresenceLocale {
  matricule: string;
  classe: string;
  statut: Statut;
  minutes_retard: number;
  motif: string;
  commentaire: string;
}

function EcranAppel({
  titre, sousTitre, formateur, salle,
  etudiantsBruts, modeClasse, onClose, onValider, isValidating,
}: {
  titre: string;
  sousTitre: string;
  formateur: string;
  salle: string;
  etudiantsBruts: AppelEtudiantItem[];
  modeClasse: boolean;
  onClose: () => void;
  onValider: (p: PresenceLocale[]) => Promise<void>;
  isValidating: boolean;
}) {
  const [recherche, setRecherche] = useState('');
  const [marques, setMarques] = useState<Record<string, PresenceLocale>>({});
  const [modalEtudiant, setModalEtudiant] = useState<AppelEtudiantItem | null>(null);

  // Init depuis les présences existantes (les non marqués restent "non marqué")
  useEffect(() => {
    const init: Record<string, PresenceLocale> = {};
    for (const e of etudiantsBruts) {
      // On considère comme déjà marqué si statut != 'present' (par défaut)
      // OU si motif/minutes_retard renseigné
      if (e.statut !== 'present' || e.minutes_retard > 0 || e.motif || e.commentaire) {
        init[e.matricule] = {
          matricule: e.matricule,
          classe: e.classe || '',
          statut: e.statut,
          minutes_retard: e.minutes_retard,
          motif: e.motif,
          commentaire: e.commentaire,
        };
      }
    }
    setMarques(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etudiantsBruts.length]);

  const filtres = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return etudiantsBruts;
    return etudiantsBruts.filter(e =>
      e.nom.toLowerCase().includes(q)
      || e.prenoms.toLowerCase().includes(q)
      || e.matricule.toLowerCase().includes(q)
    );
  }, [etudiantsBruts, recherche]);

  const nbMarques = Object.keys(marques).length;
  const nbPresents = Object.values(marques).filter(m => m.statut === 'present' || m.statut === 'retard').length;
  const nbAbsents = Object.values(marques).filter(m => m.statut === 'absent').length;
  const total = etudiantsBruts.length;
  const nbNonMarques = total - nbMarques;

  const setStatutRapide = (etu: AppelEtudiantItem, statut: Statut) => {
    setMarques(prev => ({
      ...prev,
      [etu.matricule]: {
        matricule: etu.matricule,
        classe: etu.classe || '',
        statut,
        minutes_retard: prev[etu.matricule]?.minutes_retard ?? 0,
        motif: prev[etu.matricule]?.motif ?? '',
        commentaire: prev[etu.matricule]?.commentaire ?? '',
      },
    }));
  };

  const setDetail = (matricule: string, patch: Partial<PresenceLocale>) => {
    setMarques(prev => {
      const courant = prev[matricule] || {
        matricule, classe: '', statut: 'present', minutes_retard: 0, motif: '', commentaire: '',
      };
      return { ...prev, [matricule]: { ...courant, ...patch } };
    });
  };

  const handleValider = async () => {
    // Tous les étudiants non marqués → présents par défaut
    const presences: PresenceLocale[] = etudiantsBruts.map(e => marques[e.matricule] || {
      matricule: e.matricule,
      classe: e.classe || '',
      statut: 'present' as Statut,
      minutes_retard: 0,
      motif: '',
      commentaire: '',
    });
    try {
      await onValider(presences);
      onClose();
    } catch (err: any) {
      alert(err?.data?.detail || "Erreur lors de l'enregistrement.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      {/* Header sticky */}
      <div className="sticky top-0 z-30 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3">
          <div className="flex items-start gap-3">
            <button onClick={onClose}
              className="p-2 -ml-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
              <ArrowLeft size={18} />
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base sm:text-lg text-gray-900 dark:text-white truncate">{titre}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5 flex-wrap mt-0.5">
                <Users size={11} /> {sousTitre}
              </div>
              {(formateur || salle) && (
                <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-3 mt-0.5 flex-wrap">
                  {formateur && <span className="inline-flex items-center gap-1"><User size={11} /> {formateur}</span>}
                  {salle && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {salle}</span>}
                  {!modeClasse && (
                    <span className="text-purple-600 dark:text-purple-300 inline-flex items-center gap-1 font-medium">
                      <Layers size={11} /> Tronc commun
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Recherche */}
          <div className="relative mt-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher par nom, prénom ou matricule…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Compteurs */}
          <div className="mt-2 flex items-center gap-3 text-[11px] flex-wrap">
            <span className="text-gray-600 dark:text-gray-400">Total : <span className="font-semibold text-gray-900 dark:text-white">{total}</span></span>
            <span className="text-green-600 dark:text-green-400">Présents : <span className="font-semibold">{nbPresents}</span></span>
            <span className="text-red-500 dark:text-red-400">Absents : <span className="font-semibold">{nbAbsents}</span></span>
            <span className="text-gray-500">Non marqués : <span className="font-semibold">{nbNonMarques}</span></span>
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="max-w-5xl mx-auto px-2 sm:px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-2">
        {filtres.length === 0 && (
          <div className="col-span-full text-center py-10 text-sm text-gray-500">
            Aucun étudiant ne correspond à votre recherche.
          </div>
        )}
        {filtres.map(e => {
          const marque = marques[e.matricule];
          return (
            <EtudiantAppelRow
              key={e.matricule}
              etudiant={e}
              statut={marque?.statut || null}
              minutesRetard={marque?.minutes_retard || 0}
              afficherClasse={!modeClasse}
              onMarquer={(s) => setStatutRapide(e, s)}
              onOuvrirDetail={() => setModalEtudiant(e)}
            />
          );
        })}
      </div>

      {/* Footer fixe */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-lg">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-3">
          <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 flex-1">
            <span className="font-bold">{nbPresents + nbAbsents}</span>
            <span className="text-gray-500"> / {total} marqués</span>
          </div>
          <button onClick={handleValider} disabled={isValidating}
            className="px-4 sm:px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2">
            <Save size={14} /> {isValidating ? 'Enregistrement…' : "Valider l'appel"}
          </button>
        </div>
      </div>

      {/* Modal détail */}
      {modalEtudiant && (
        <ModalDetailPresence
          etudiant={modalEtudiant}
          marque={marques[modalEtudiant.matricule]}
          onClose={() => setModalEtudiant(null)}
          onValider={(patch) => {
            setDetail(modalEtudiant.matricule, patch);
            setModalEtudiant(null);
          }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function ChargementPlein({ label }: { label: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-gray-500 text-sm">
      {label}
    </div>
  );
}

export { STATUT_META };
